export type OutlookEventTimes = {
  start: { dateTime: string; timeZone?: string };
  end: { dateTime: string; timeZone?: string };
  isAllDay?: boolean;
};

/** Outlook all-day events end at midnight of the following day. */
export function inclusiveEventEnd(event: OutlookEventTimes): string {
  if (!event.isAllDay) return event.end.dateTime;
  const end = new Date(`${event.end.dateTime.slice(0, 10)}T12:00:00Z`);
  end.setUTCDate(end.getUTCDate() - 1);
  return end.toISOString().slice(0, 10);
}

/** Graph returns UTC times without a zone suffix unless asked otherwise. */
function toMs(value: string | null | undefined, timeZone?: string): number | null {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const zoned = timeZone === 'UTC' && /T\d/.test(raw) && !/(Z|[+-]\d{2}:?\d{2})$/.test(raw) ? `${raw}Z` : raw;
  const ms = Date.parse(zoned);
  return Number.isFinite(ms) ? ms : null;
}

const LONDON = 'Europe/London';

function londonOffsetMinutes(at: Date): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: LONDON, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  }).formatToParts(at);
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value || 0);
  const wall = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'));
  return Math.round((wall - Math.floor(at.getTime() / 60000) * 60000) / 60000);
}

/** The UTC instant of a UK wall-clock day and time (BST-aware). */
export function londonWallClockToIso(day: string, time: string): string {
  const guess = Date.parse(`${day}T${time}:00Z`);
  let ms = guess - londonOffsetMinutes(new Date(guess)) * 60000;
  const settled = londonOffsetMinutes(new Date(ms));
  ms = guess - settled * 60000;
  return new Date(ms).toISOString();
}

/** UK time of day of a saved stamp, or null for a whole-day stamp (UK or UTC midnight). */
function londonTimeOf(value: string | null | undefined): string | null {
  const ms = toMs(value);
  if (ms == null) return null;
  const at = new Date(ms);
  if (at.getUTCHours() === 0 && at.getUTCMinutes() === 0) return null;
  const time = at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: LONDON });
  return time === '00:00' ? null : time;
}

/**
 * The booking dates for an Outlook event. An all-day event has no time, so a start time the
 * booking already had is kept (and carried to the new day if the event moved).
 */
export function outlookBookingDates(
  outlook: OutlookEventTimes,
  savedStart?: string | null,
): { event_date: string; end_date: string } | null {
  const startMs = toMs(outlook.start.dateTime, outlook.start.timeZone);
  const endRaw = inclusiveEventEnd(outlook);
  const endMs = toMs(endRaw, outlook.isAllDay ? undefined : outlook.end.timeZone);
  if (startMs == null || endMs == null) return null;
  if (outlook.isAllDay) {
    const time = londonTimeOf(savedStart);
    return {
      event_date: time ? londonWallClockToIso(outlook.start.dateTime.slice(0, 10), time) : new Date(startMs).toISOString(),
      end_date: endRaw,
    };
  }
  return { event_date: new Date(startMs).toISOString(), end_date: new Date(endMs).toISOString() };
}

/** Saved event dates that differ from the Outlook event, or null when they already match. */
export function outlookDateChanges(
  saved: { event_date?: string | null; end_date?: string | null },
  outlook: OutlookEventTimes,
): { event_date: string; end_date: string } | null {
  const next = outlookBookingDates(outlook, saved.event_date);
  if (!next) return null;
  const same = toMs(saved.event_date) === toMs(next.event_date) && toMs(saved.end_date) === toMs(next.end_date);
  return same ? null : next;
}
